import {
    AppTable,
    FlexRow,
    PaginationComponent,
} from 'components'
import React, { useMemo } from 'react'
import { useTabletAndBelowMediaQuery } from 'layout'
import { Card } from 'semantic-ui-react'
import styled from 'styled-components'

const PaginationWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    flex-wrap: wrap;
    gap: 12px;

    .pagination-info {
        color: #666;
        font-size: 14px;
    }
`

const AuditLogCard = ({ item }) => {
    return (
        <Card fluid>
            <Card.Content>
                <Card.Description>
                    <FlexRow>
                        <Card.Meta>User:</Card.Meta>&nbsp; {item.user}
                    </FlexRow>
                    <FlexRow>
                        <Card.Meta>Action:</Card.Meta>&nbsp; {item.action}
                    </FlexRow>
                    {item.relatedAsset && (
                        <FlexRow>
                            <Card.Meta>Related Asset:</Card.Meta>&nbsp; {item.relatedAsset}
                        </FlexRow>
                    )}
                </Card.Description>
            </Card.Content>
        </Card>
    )
}

// Group audit logs by date for mobile view
const groupByDate = (data) => {
    const grouped = {}
    data.forEach(item => {
        const date = new Date(item.date).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        })
        if (!grouped[date]) {
            grouped[date] = []
        }
        grouped[date].push(item)
    })
    return grouped
}

export const AssetAuditLogList = ({
    data,
    filter,
    pageChanged,
    totalCount,
}) => {
    const isMobile = useTabletAndBelowMediaQuery()

    const formatDate = (dateString) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        })
    }

    // Group data by date for mobile view
    const groupedData = isMobile ? groupByDate(data) : null

    const columns = useMemo(
        () => [
            {
                title: 'DATE',
                key: 'date',
                textAlign: 'left',
                render: item => formatDate(item.date),
            },
            {
                title: 'USER',
                key: 'user',
                textAlign: 'left',
            },
            {
                title: 'ACTION',
                key: 'action',
                textAlign: 'left',
            },
            {
                title: 'RELATED ASSET',
                key: 'relatedAsset',
                textAlign: 'left',
                render: item => item.relatedAsset || '-',
            },
        ],
        [],
    )

    const startIndex = ((filter?.PageNumber || 1) - 1) * (filter?.PageSize || 25) + 1
    const endIndex = Math.min(
        (filter?.PageNumber || 1) * (filter?.PageSize || 25),
        totalCount || 0,
    )

    const handlePageChange = (newPage) => {
        if (typeof newPage === 'number') {
            pageChanged({
                ...filter,
                PageNumber: newPage,
            })
        } else {
            pageChanged(newPage)
        }
    }

    if (isMobile && groupedData) {
        return (
            <>
                {Object.keys(groupedData).map(date => (
                    <div key={date} style={{ marginBottom: '20px' }}>
                        <div
                            style={{
                                fontWeight: 600,
                                fontSize: '16px',
                                marginBottom: '12px',
                                color: '#333',
                            }}
                        >
                            {date}
                        </div>
                        {groupedData[date].map(item => (
                            <AuditLogCard key={item.id} item={item} />
                        ))}
                    </div>
                ))}
                {totalCount > 0 && (
                    <PaginationWrapper>
                        <div className="pagination-info">
                            Showing {startIndex}-{endIndex} of {totalCount} audit logs
                        </div>
                        <div className="w-100 justify-content-end d-flex">
                            <PaginationComponent
                                activePage={filter?.PageNumber || 1}
                                pageChanged={handlePageChange}
                                pageSize={filter?.PageSize || 25}
                                totalCount={totalCount}
                            />
                        </div>
                    </PaginationWrapper>
                )}
            </>
        )
    }

    return (
        <>
            <AppTable
                data={data}
                columns={columns}
                pageChanged={handlePageChange}
                filter={filter}
                totalCount={totalCount}
                emptyMessage="No audit logs found"
                mobileCard={<AuditLogCard />}
                hidePagination
            />
            {totalCount > 0 && (
                <PaginationWrapper>
                    <div className="pagination-info">
                        Showing {startIndex}-{endIndex} of {totalCount} audit logs
                    </div>
                    <div className="w-100 justify-content-end d-flex">
                        <PaginationComponent
                            activePage={filter?.PageNumber || 1}
                            pageChanged={handlePageChange}
                            pageSize={filter?.PageSize || 25}
                            totalCount={totalCount}
                        />
                    </div>
                </PaginationWrapper>
            )}
        </>
    )
}
